import {
  Alert,
  Box,
  IconButton,
  InputAdornment,
  Snackbar,
  Link,
  Button
} from "@mui/material";
import { PrimaryButton, PrimaryButtonText } from "../../components/ui/Button";
import { InputField } from "../../components/ui/Inputs";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { ReactComponent as EyeOffIcon } from "../../assets/icons/auth/eye-off.svg";
import { ReactComponent as EyeIcon } from "../../assets/icons/auth/eye.svg";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { SubmitHandler, useForm } from "react-hook-form";
import { ROUTES } from "../../routes/routerConfig";
import { authSelector, setUserAuth, clearOtpUserData, setPasswordType } from "../../services/redux/slices/authSlice";
import service from "../../services/api/service";
import {
  setStatusPopup,
} from "../../services/redux/slices/statusPopupSlice"; import regex from "../../data/regex";
import { encrypt, decrypt } from "../../utils/crypto";
import InputLabelComp from "../../components/ui/InputLabelComp";
import ButtonLoader from "../../components/ui/ButtonLoader";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { trackEvent } from "../../utils/ga";

interface FormInputs {
  password: string;
  confirmPassword: string;
}

type props = {
  setNextStep: (step: string) => void,
}

const SetPassword = ({ setNextStep }: props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = useForm<FormInputs>(); // Update to use FormInputs here
  const [severity, setSeverity] = useState<
    "error" | "warning" | "info" | "success"
  >("success");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const newUserData: any = useSelector((state: any) => state.keyRegistrar);

  const { userProfile, setPasswordKey, SetPasswordArray, resetToken } = useSelector(authSelector);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSnackbarOpen = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const password = watch("password");

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleBack = () => {
    setNextStep('');
  };

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    trackEvent('Enter_SetPassword_Continue', decrypt(userProfile?.id || newUserData?.newUserData?.mobile), {
      label: 'Enter_SetPassword_Continue',
      category: 'Enter_SetPassword_Continue',
      action: "Enter_SetPassword_Continue",
    });

    let cifNumberValue = "";
    if (newUserData?.newUserData?.mobile) {
      cifNumberValue = encrypt(newUserData?.newUserData?.mobile);
    } else {
      cifNumberValue = "";
      if (userProfile?.cifNumber) {
        cifNumberValue = userProfile?.cifNumber;
      } else if (userProfile?.email && userProfile?.phoneNumber) {
        cifNumberValue = userProfile?.phoneNumber;
      }
    }
    let filterCifNumber = SetPasswordArray?.filter((item: any) =>  decrypt(item?.cifNumber) === decrypt(userProfile?.cifNumber));

    setIsLoading(true);
    const newPassword = data.confirmPassword;
    const password = data.password;
    const payload: Record<string, any> = {
      cifNumber: cifNumberValue,
      newPassword: encrypt(newPassword),
      password: encrypt(password),
      passwordType: "set",
      key: SetPasswordArray.length > 1
      ? filterCifNumber?.[0]?.id
      : setPasswordKey,
      resetToken: filterCifNumber?.[0]?.resetToken || SetPasswordArray?.[0]?.resetToken || resetToken,
    };
    service?.auth?.setUserPassword(payload).then((res: any) => {
      const token = res?.data?.data?.token;
      setSeverity("success");
      handleSnackbarOpen("Your password is updated successfully");
      dispatch(clearOtpUserData());
      dispatch(setPasswordType(res?.data?.data?.passwordType));
      // navigate(ROUTES.Login.passwordCreated.locationPath, { replace: true });
      setNextStep(ROUTES?.Login?.passwordCreated?.path);
      setIsLoading(false);
    }).catch((error: any) => {
      setSeverity("error");
      // handleSnackbarOpen(error.response.data.message);
      setIsLoading(false);
      dispatch(
        setStatusPopup({
          status: "error",
          message: error.response.data.message,
        })
      );
    });
  };

  const onKeyDownHandler = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      // Prefer 'e.key' over 'e.keyCode'
      e.preventDefault(); // Prevent default form submission behavior
      handleSubmit(onSubmit)(); // Properly invoke handleSubmit with the onSubmit handler
    }
  };

  return (
    <Box>
      <PrimaryButtonText
        startIcon={<ArrowBack color="inherit" fontSize="small" />}
        onClick={handleBack}
      >
        Back
      </PrimaryButtonText>
      <Box className="main-wrapper">
        <Box className="head-section">
          <Box className="heading"> Setup your Password <span className="fontsize">🔒</span></Box>
          <Box className="sub-heading">
            Once your password is set, you can securely access your account.
          </Box>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <InputLabelComp title="New Password" css={false} />
          <InputField
            type={showPassword ? "text" : "password"}
            required
            fullWidth
            autoFocus
            // onKeyDown={(e: any) => onKeyDownHandler(e)}
            placeholder="New password"
            {...register("password", {
              required: "Password is required",
              minLength: {
                value: 8,
                message: "Password must be at least 8 characters long",
              },
              maxLength: {
                value: 15,
                message: "Password must be at most 15 characters long",
              },
              pattern: {
                value: regex?.password,
                message: "Password must contain one capital letter and symbol @$!%*?& or numbers"
              }
            })}
            error={!!errors.password}
            helperText={errors.password && errors.password.message}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <EyeIcon /> : <EyeOffIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <InputLabelComp title="Confirm Password" css={false} />
          <InputField
            autoComplete="off"
            type={showPassword ? "text" : "password"}
            required
            fullWidth
            placeholder="Confirm password"
            onKeyDown={(e: any) => onKeyDownHandler(e)}
            {...register("confirmPassword", {
              required: "Please confirm your password",
              validate: (value) =>
                value === password || "Passwords do not match",
            })}
            error={!!errors.confirmPassword}
            helperText={
              errors.confirmPassword && errors.confirmPassword.message
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <EyeIcon /> : <EyeOffIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box
            component="ul"
            style={{
              marginTop: "15px",
              marginBottom: "15px",
              marginLeft: "-20px",
              fontSize: "15px",
            }}
          >
            <Box component="li">Between 8-15 characters without spacing</Box>
            <Box component="li">
              Contains a combination of uppercase letters, lowercase letters,
              numbers, and special characters
            </Box>
          </Box>
          <PrimaryButton
            type="button" // Change type to "submit"
            onClick={handleSubmit(onSubmit)}
            fullWidth
            disabled={isLoading}
          >
            <ButtonLoader title="Set New Password" isLoading={isLoading} />
          </PrimaryButton>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginTop: "16px",
            }}
          >

            <Button
              onClick={(e) => {
                if (e.button === 0) handleBack(); // Only handle left mouse click
              }}
              id="back-to-login-link"
              sx={{
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                color: "rgba(0, 179, 181, 1)",
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                  backgroundColor: "transparent"
                },
                textAlign: "center",
                textTransform: "none" // Add this line to prevent text transformation
              }}
            >
              <ChevronLeftIcon sx={{ marginRight: "4px", fontSize: "15px" }} />
              Back to login
            </Button>

          </Box>
        </form>
      </Box>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={snackbarOpen}
        autoHideDuration={6000}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SetPassword;
